import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser, hasRole } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Search, FileDown, Printer, Plus, Minus, ChevronDown, ChevronUp, ShoppingCart as CartIcon, User, Package, Trash2, Settings2, ArrowRightLeft, Send, Radio, MapPin, CreditCard, Copy, ExternalLink, BadgePercent, Banknote } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import OrderStripeEvents from "@/components/OrderStripeEvents";
import { OrderPaymentReferences } from "@/components/OrderPaymentReferences";
import StatusPill, { ORDER_STATUS_OPTIONS } from "@/components/StatusPill";
import { downloadCsv, downloadAsPdf } from "@/lib/exporters";
import Pagination from "@/components/Pagination";
import { useResponsivePageSize } from "@/hooks/useResponsivePageSize";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";
import { OrderSortHeader } from "@/components/OrderSortHeader";
import { ORDER_SOURCE_LABELS, orderSource, orderItemsText, orderPartnerText, sortOrders } from "@/lib/orderSorting";

const EMPTY_ORDER = { client_id: null, client: null, items: [], notes: "" };
const MODIFICATION_JUSTIFICATIONS = ["Forgot something", "Wrong Item", "See Notes"];
const DELETION_JUSTIFICATIONS = ["Customer Canceled", "Test Order", "Out Of Stock", "Canceled by MPire"];

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [unpaidOpen, setUnpaidOpen] = useState(false);
  const [archivesOpen, setArchivesOpen] = useState(false);
  const [openArchiveWeeks, setOpenArchiveWeeks] = useState({});
  const [unpaidSortKey, setUnpaidSortKey] = useState("created_at");
  const [unpaidSortDir, setUnpaidSortDir] = useState("desc");
  const [sourceFilter, setSourceFilter] = useState("all"); // all | direct_site | client_portal | admin | wholesale
  const [etaOrder, setEtaOrder] = useState(null);
  // GPS Dispatch — active DPs live-tracked; opens a map dialog
  const [activeEps, setActiveEps] = useState([]);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [assigningNearestId, setAssigningNearestId] = useState(null);
  // Real-time "Take payment" — copy/open a Stripe checkout link on the spot
  const [payOrder, setPayOrder] = useState(null);
  const [payUrl, setPayUrl] = useState("");
  const [payLoadingId, setPayLoadingId] = useState(null);

  const canManageOrder = () => isAdminUser(user) || user?.role === "personnel" || user?.role === "distributing_partner";
  const canTakePayment = (o) => canManageOrder(o);

  const takePayment = async (o) => {
    setPayLoadingId(o.id);
    try {
      const { data } = await api.post(`/orders/${o.id}/payment-link`);
      setPayOrder(o);
      setPayUrl(data.checkout_url);
      if (data?.sms?.sent) {
        toast.success("Payment link texted to the client");
      } else if (data?.sms?.reason === "vonage_not_configured") {
        toast.info("Link ready — copy or open it below (SMS not configured yet)");
      } else {
        toast.info("Payment link ready — copy or open it below");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create a payment link");
    } finally {
      setPayLoadingId(null);
    }
  };

  const copyPayUrl = async () => {
    try {
      await navigator.clipboard.writeText(payUrl);
      toast.success("Payment link copied");
    } catch {
      toast.error("Copy failed — select and copy the link manually");
    }
  };

  const loadActiveEps = React.useCallback(async () => {
    try {
      const { data } = await api.get("/dispatch/active-eps");
      setActiveEps(data || []);
    } catch { setActiveEps([]); }
  }, []);

  useEffect(() => {
    if (!isAdminUser(user)) return;
    loadActiveEps();
    const t = setInterval(loadActiveEps, 30000);
    return () => clearInterval(t);
  }, [user, loadActiveEps]);

  const assignNearest = async (order) => {
    setAssigningNearestId(order.id);
    try {
      const { data } = await api.post(`/dispatch/orders/${order.id}/assign-nearest`);
      toast.success(`Assigned to ${data.assigned_ep_name} · ${data.distance_mi} mi away`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Assign failed");
    } finally {
      setAssigningNearestId(null);
    }
  };
  const [eta, setEta] = useState({ minutes: 30, message: "" });

  // Manage-order (edit + reassign) state
  const [manageOrder, setManageOrder] = useState(null);
  const [manageStatus, setManageStatus] = useState("");
  const [manageNotes, setManageNotes] = useState("");
  const [manageFulfiller, setManageFulfiller] = useState("warehouse");
  const [manageItems, setManageItems] = useState([]);
  const [manageProductQuery, setManageProductQuery] = useState("");
  const [manageAddQty, setManageAddQty] = useState(1);
  const [manualDiscount, setManualDiscount] = useState({ discount_type: "percentage", amount: "", reason: "" });
  const [createDiscount, setCreateDiscount] = useState({ discount_type: "percentage", amount: "", reason: "" });
  const [discountSaving, setDiscountSaving] = useState(false);
  const [modificationJustification, setModificationJustification] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteJustification, setDeleteJustification] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [cashOrder, setCashOrder] = useState(null);
  const [archiveViewOrder, setArchiveViewOrder] = useState(null);
  const [cashReceived, setCashReceived] = useState("");
  const [cashNote, setCashNote] = useState("");
  const [cashSaving, setCashSaving] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = useResponsivePageSize(10, 25);

  // Add-order form state
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_ORDER);
  const [clientQuery, setClientQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [showClientList, setShowClientList] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [o, c, p, pp] = await Promise.all([
      api.get("/orders", { params: { all_results: true } }).then(r => r.data),
      api.get("/clients").then(r => r.data).catch(() => []),
      api.get("/products").then(r => r.data).catch(() => []),
      api.get("/endpoint-partners").then(r => r.data).catch(() => []),
    ]);
    setOrders(o); setClients(c); setProducts(p); setPartners(pp);
  };
  useEffect(() => { load(); }, []);

  const clientsById = useMemo(() => new Map(clients.map(client => [client.id, client])), [clients]);
  const partnersById = useMemo(() => new Map(partners.map(partner => [partner.id, partner])), [partners]);

  // Client typeahead: top matches, capped at 8
  const matchedClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return [];
    return clients.filter(c =>
      c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [clients, clientQuery]);

  // Product search
  const matchedProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return [];
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q) || p.vendor_name?.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [products, productQuery]);
  const matchedManageProducts = useMemo(() => {
    const q = manageProductQuery.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q) || p.vendor_name?.toLowerCase().includes(q)).slice(0, 8);
  }, [products, manageProductQuery]);

  const cartTotal = useMemo(
    () => draft.items.reduce((s, i) => s + i.qty * i.unit_price, 0),
    [draft.items]
  );
  const createDiscountAmount = Number(createDiscount.amount) > 0 ? (createDiscount.discount_type === "percentage" ? Math.min(cartTotal, cartTotal * Number(createDiscount.amount) / 100) : Math.min(cartTotal, Number(createDiscount.amount))) : 0;
  const createDiscountedTotal = cartTotal - createDiscountAmount;

  const pickClient = (c) => {
    setDraft({ ...draft, client_id: c.id, client: c });
    setClientQuery(""); setShowClientList(false);
  };

  const addItem = (p) => {
    // if already in cart, bump qty
    const existing = draft.items.find(i => i.product_id === p.id);
    let next;
    if (existing) {
      next = draft.items.map(i => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
    } else {
      next = [...draft.items, { product_id: p.id, name: p.name, sku: p.sku, qty: 1,
        unit_price: p.wholesale_price || 0, stock: p.warehouse_stock || 0 }];
    }
    setDraft({ ...draft, items: next });
    setProductQuery("");
  };
  const setItemQty = (pid, qty) => {
    const q = Math.max(0, Number(qty) || 0);
    setDraft({ ...draft, items: q === 0
      ? draft.items.filter(i => i.product_id !== pid)
      : draft.items.map(i => i.product_id === pid ? { ...i, qty: q } : i) });
  };
  const removeItem = (pid) => setDraft({ ...draft, items: draft.items.filter(i => i.product_id !== pid) });

  const openCreate = () => { setDraft(EMPTY_ORDER); setClientQuery(""); setProductQuery(""); setCreateOpen(true); };

  const submitOrder = async () => {
    if (!draft.client_id) { toast.error("Please pick a client"); return; }
    if (draft.items.length === 0) { toast.error("Please add at least one product"); return; }
    setSubmitting(true);
    try {
      const items = draft.items.map(i => ({
        product_id: i.product_id, name: i.name, qty: i.qty, unit_price: i.unit_price,
      }));
      await api.post("/orders", { client_id: draft.client_id, items, notes: draft.notes, manual_discount: Number(createDiscount.amount) && createDiscount.reason.trim() ? { ...createDiscount, amount: Number(createDiscount.amount) } : null });
      toast.success(`Order created for ${draft.client?.name} · $${cartTotal.toFixed(2)}`);
      setCreateOpen(false); setDraft(EMPTY_ORDER); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const sourceCounts = useMemo(() => {
    const c = { all: orders.length, direct_site: 0, client_portal: 0, wholesale: 0, admin: 0 };
    orders.forEach((o) => { c[orderSource(o)] = (c[orderSource(o)] || 0) + 1; });
    return c;
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const arr = orders.filter(o => {
      if (String(o.status || "").toLowerCase() === "fulfilled") return false;
      if (sourceFilter !== "all" && orderSource(o) !== sourceFilter) return false;
      if (!q) return true;
      const client = clientsById.get(o.client_id);
      return [o.client_name, client?.company, ...(o.items || o.products || []).map(i => i.name)].some(s => s?.toLowerCase().includes(q));
    });
    return sortOrders(arr, sortKey, sortDir, clientsById, partnersById);
  }, [orders, search, sortKey, sortDir, sourceFilter, clientsById, partnersById]);
  useEffect(() => { setPage(0); }, [search, sortKey, sortDir, sourceFilter, PAGE_SIZE]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const paged = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const setSort = (k) => { setPage(0); setSortKey(k); setSortDir(d => sortKey === k && d === "asc" ? "desc" : "asc"); };
  const sortHeaderProps = { activeKey: sortKey, direction: sortDir, onSort: setSort };
  const unpaidOrders = useMemo(() => sortOrders(orders.filter((order) => order.payment_status !== "paid"), unpaidSortKey, unpaidSortDir, clientsById, partnersById), [orders, unpaidSortKey, unpaidSortDir, clientsById, partnersById]);
  const unpaidTotal = useMemo(() => unpaidOrders.reduce((sum, order) => sum + (Number(order.total) || 0), 0), [unpaidOrders]);
  const setUnpaidSort = (key) => setUnpaidSortDir((direction) => { setUnpaidSortKey(key); return unpaidSortKey === key && direction === "asc" ? "desc" : "asc"; });
  const unpaidSortHeaderProps = { activeKey: unpaidSortKey, direction: unpaidSortDir, onSort: setUnpaidSort };
  const archiveWeeks = useMemo(() => { const cutoff = Date.now() - 30 * 86400000; const groups = {}; orders.filter(o => String(o.status || "").toLowerCase() === "fulfilled" && new Date(o.created_at).getTime() >= cutoff).forEach(o => { const d = new Date(o.created_at); const sunday = new Date(d); sunday.setDate(d.getDate() - d.getDay()); sunday.setHours(0, 0, 0, 0); const key = sunday.toISOString().slice(0, 10); (groups[key] ||= []).push(o); }); return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a)); }, [orders]);

  const sendEta = async () => {
    try {
      await api.patch(`/orders/${etaOrder.id}/eta`, { eta_minutes: Number(eta.minutes), message: eta.message });
      toast.success("ETA sent"); setEtaOrder(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const fulfill = async (id) => {
    try { await api.patch(`/orders/${id}/fulfill`); toast.success("Marked fulfilled"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const restoreArchivedOrder = async (order) => {
    if (!window.confirm(`Change ${order.id?.slice(-8) || "this order"} to Ordered and return it to Client Orders?`)) return;
    try { await api.patch(`/orders/${order.id}/status`, { status: "Ordered" }); toast.success("Order returned to Client Orders"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to update order status"); }
  };

  const openManage = (o) => {
    setManageOrder(o);
    const status = (o.status || "ordered").toLowerCase();
    setManageStatus(status === "confirmed" ? "dropped off" : status);
    setManageNotes(o.notes || "");
    setManageFulfiller(o.fulfilled_by || (o.assignment === "warehouse" ? "warehouse" : "warehouse"));
    setManageItems((o.items || o.products || []).map((item) => ({ ...item })));
    setManageProductQuery(""); setManageAddQty(1);
    setManualDiscount({ discount_type: "percentage", amount: "", reason: "" });
    setModificationJustification("");
  };
  const isImmutable = manageOrder?.payment_status === "paid";
  const updateManageQty = (productId, nextQty) => setManageItems((items) => nextQty <= 0 ? items.filter((item) => item.product_id !== productId) : items.map((item) => item.product_id === productId ? { ...item, qty: nextQty } : item));
  const addManageProduct = (product) => {
    const qty = Math.max(1, Number(manageAddQty) || 1);
    setManageItems((items) => {
      const current = items.find((item) => item.product_id === product.id);
      return current ? items.map((item) => item.product_id === product.id ? { ...item, qty: Number(item.qty) + qty } : item) : [...items, { product_id: product.id, name: product.name, sku: product.sku, qty, unit_price: Number(product.wholesale_price || 0) }];
    });
    setManageProductQuery(""); setManageAddQty(1);
  };
  const manageSubtotal = manageItems.reduce((sum, item) => sum + Number(item.qty || 0) * Number(item.unit_price || 0), 0);
  const managedDiscountAmount = (() => { const d = manageOrder?.discount; if (!d) return 0; return d.discount_type === "percentage" ? manageSubtotal * (Number(d.amount || 0) / 100) : Math.min(Number(d.amount || 0), manageSubtotal); })();
  const manageTipAmount = Number(manageOrder?.tip_amount || manageOrder?.tip?.amount || 0);
  const manageTotal = Math.max(0, manageSubtotal - managedDiscountAmount) + manageTipAmount;

  const saveManage = async () => {
    if (!manageOrder) return;
    if (!modificationJustification) { toast.error("Choose a modification justification before saving"); return; }
    if (modificationJustification === "See Notes" && manageNotes.trim().length < 5) { toast.error("See Notes requires at least 5 characters in Notes"); return; }
    try {
      await api.patch(`/orders/${manageOrder.id}`, {
        status: manageStatus,
        notes: manageNotes,
        items: isImmutable ? undefined : manageItems.map((item) => ({ product_id: item.product_id, qty: Number(item.qty) })),
        justification: modificationJustification,
      });
      // Reassign is a separate endpoint; only fire if changed
      const currentFulfiller = manageOrder.fulfilled_by || (manageOrder.assignment === "warehouse" ? "warehouse" : "warehouse");
      if (isAdminUser(user) && manageFulfiller !== currentFulfiller) {
        await api.post(`/orders/${manageOrder.id}/reassign`, { fulfiller: manageFulfiller });
      }
      toast.success("Order updated");
      setManageOrder(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const openDelete = (order) => { setDeleteTarget(order); setDeleteJustification(""); };
  const deleteOrder = async () => {
    if (!deleteTarget) return;
    if (!deleteJustification) { toast.error("Choose a deletion justification"); return; }
    setDeleteSaving(true);
    try {
      await api.delete(`/orders/${deleteTarget.id}`, { data: { justification: deleteJustification } });
      toast.success("Order deleted and the justification was recorded");
      setDeleteTarget(null); setManageOrder(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete order"); }
    finally { setDeleteSaving(false); }
  };
  const openCashPayment = (order) => { setCashOrder(order); setCashReceived(Number(order.total || 0).toFixed(2)); setCashNote(""); };
  const cashAmount = Number(cashReceived || 0);
  const cashDue = Number(cashOrder?.total || 0);
  const changeDue = Math.max(0, cashAmount - cashDue);
  const confirmCashPayment = async () => {
    if (!cashOrder) return;
    if (!cashAmount || cashAmount < cashDue) { toast.error(`Cash received must cover $${cashDue.toFixed(2)}`); return; }
    setCashSaving(true);
    try {
      const { data } = await api.post(`/orders/${cashOrder.id}/cash-payment`, { amount_received: cashAmount, note: cashNote });
      setCashOrder(null); load();
      toast.success(`Cash payment recorded${data.receipt.change_due ? ` · $${Number(data.receipt.change_due).toFixed(2)} change due` : ""}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record cash payment"); }
    finally { setCashSaving(false); }
  };
  const applyManualDiscount = async () => {
    if (!manageOrder) return;
    if (!Number(manualDiscount.amount) || !manualDiscount.reason.trim()) { toast.error("Enter a discount amount and short reason"); return; }
    setDiscountSaving(true);
    try {
      const { data } = await api.post(`/orders/${manageOrder.id}/manual-discount`, { ...manualDiscount, amount: Number(manualDiscount.amount) });
      setManageOrder(data);
      setManualDiscount({ discount_type: "percentage", amount: "", reason: "" });
      toast.success("Manual discount applied and payment link refreshed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not apply discount"); }
    finally { setDiscountSaving(false); }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <p className="label-eyebrow">Dispatch</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Client Orders</h1>
          <p className="text-gray-500 mt-1 text-sm">{orders.length} total · assign, ETA, and fulfill.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <Input className="pl-8 w-64" placeholder="Search company / client / product…"
                   value={search} onChange={e => setSearch(e.target.value)} data-testid="orders-search" />
          </div>
          {isAdminUser(user) && (
            <>
              <Button variant="outline" onClick={() => downloadCsv("/orders/export.csv", "orders.csv")} data-testid="export-orders-csv"><FileDown size={14} className="mr-1" /> CSV</Button>
              <label className="inline-flex">
                <input type="file" accept=".csv" className="hidden" data-testid="import-orders-csv"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f);
                    try { const { data } = await api.post("/orders/import.csv", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.created} orders`); load(); }
                    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } e.target.value = ""; }} />
                <span className="inline-flex items-center cursor-pointer px-4 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)]">Import CSV</span>
              </label>
              <Button variant="outline" onClick={() => downloadAsPdf("orders-print", "orders.pdf")} data-testid="export-orders-pdf"><Printer size={14} className="mr-1" /> PDF</Button>
              <Button
                variant="outline"
                onClick={() => setDispatchOpen(true)}
                data-testid="open-dispatch-map"
                className={activeEps.length > 0 ? "border-emerald-400 text-emerald-700" : ""}
              >
                <Radio size={14} className="mr-1" />
                Dispatch
                <span className={`ml-1 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${activeEps.length > 0 ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-600"}`}>
                  {activeEps.length}
                </span>
              </Button>
            </>
          )}
          {canManageOrder() && (
            <>
              <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setDraft(EMPTY_ORDER); }}>
                <DialogTrigger asChild><Button className="btn-brand" onClick={openCreate} data-testid="create-order-button"><Plus size={14} className="mr-1" /> New order</Button></DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>New client order</DialogTitle><DialogDescription>Create an order for an existing client and select its products.</DialogDescription></DialogHeader>

                  {/* Step 1 — Client picker */}
                  <div className="border border-[var(--border)] rounded-lg p-3 mb-3"><Label className="mb-2 block">Discount (optional)</Label><div className="grid grid-cols-2 gap-2"><select value={createDiscount.discount_type} onChange={e => setCreateDiscount({ ...createDiscount, discount_type: e.target.value })} className="border rounded px-2" data-testid="create-discount-type"><option value="percentage">Percent off</option><option value="fixed">Dollar amount off</option></select><Input type="number" min="0" value={createDiscount.amount} onChange={e => setCreateDiscount({ ...createDiscount, amount: e.target.value })} placeholder="Amount" data-testid="create-discount-amount" /></div><Input className="mt-2" value={createDiscount.reason} onChange={e => setCreateDiscount({ ...createDiscount, reason: e.target.value })} placeholder="Reason for this adjustment" data-testid="create-discount-reason" /><div className="mt-2 text-sm">Subtotal ${cartTotal.toFixed(2)} · Discount −${createDiscountAmount.toFixed(2)} · <b>Total ${createDiscountedTotal.toFixed(2)}</b></div></div>
                  <div className="border border-[var(--border)] rounded-lg p-3 mb-3">
                    <Label className="flex items-center gap-1 mb-2"><User size={13} /> 1. Client</Label>
                    {draft.client ? (
                      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="selected-client">
                        <div>
                          <div className="font-semibold capitalize">{draft.client.name}</div>
                          <div className="text-xs text-gray-500">{draft.client.company || draft.client.email || "—"}</div>
                        </div>
                        <button onClick={() => { setDraft({ ...draft, client: null, client_id: null }); }}
                                className="text-red-600 text-xs hover:underline" data-testid="clear-client">Change</button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search size={13} className="absolute left-3 top-2.5 text-gray-400" />
                        <Input className="pl-8" placeholder="Type name, company or email…"
                               value={clientQuery} onChange={e => { setClientQuery(e.target.value); setShowClientList(true); }}
                               onFocus={() => setShowClientList(true)}
                               data-testid="client-typeahead" />
                        {showClientList && matchedClients.length > 0 && (
                          <div className="absolute z-10 top-full mt-1 w-full bg-white border border-[var(--border)] rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="client-suggestions">
                            {matchedClients.map(c => (
                              <button key={c.id} type="button" onClick={() => pickClient(c)}
                                      className="w-full text-left p-2 hover:bg-gray-50 border-b border-[var(--border)] last:border-b-0"
                                      data-testid={`client-suggest-${c.id}`}>
                                <div className="font-medium capitalize text-sm">{c.name}</div>
                                <div className="text-xs text-gray-500 truncate">{c.company || c.email || "—"}</div>
                              </button>
                            ))}
                          </div>
                        )}
                        {clientQuery && matchedClients.length === 0 && <p className="text-xs text-gray-400 mt-1">No match. Try a different keyword.</p>}
                      </div>
                    )}
                  </div>

                  {/* Step 2 — Product picker */}
                  <div className="border border-[var(--border)] rounded-lg p-3 mb-3">
                    <Label className="flex items-center gap-1 mb-2"><Package size={13} /> 2. Add products</Label>
                    <div className="relative mb-2">
                      <Search size={13} className="absolute left-3 top-2.5 text-gray-400" />
                      <Input className="pl-8" placeholder="Search by name, SKU or vendor…"
                             value={productQuery} onChange={e => setProductQuery(e.target.value)}
                             data-testid="product-typeahead" />
                      {productQuery && matchedProducts.length > 0 && (
                        <div className="absolute z-10 top-full mt-1 w-full bg-white border border-[var(--border)] rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="product-suggestions">
                          {matchedProducts.map(p => (
                            <button key={p.id} type="button" onClick={() => addItem(p)}
                                    className="w-full flex items-center justify-between p-2 hover:bg-gray-50 border-b border-[var(--border)] last:border-b-0 text-left"
                                    data-testid={`product-suggest-${p.id}`}>
                              <div className="min-w-0">
                                <div className="text-sm capitalize truncate">{p.name}</div>
                                <div className="text-xs text-gray-500">{p.sku} · stock {p.warehouse_stock ?? 0}</div>
                              </div>
                              <div className="text-sm font-semibold text-emerald-600 tabular-nums ml-2">${(p.wholesale_price || 0).toFixed(2)}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Cart */}
                    {draft.items.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">No items yet — search a product above to add.</p>
                    ) : (
                      <div className="space-y-1" data-testid="order-cart">
                        {draft.items.map(i => (
                          <div key={i.product_id} className="flex items-center gap-2 py-1.5 border-b border-[var(--border)] last:border-0" data-testid={`cart-line-${i.product_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm capitalize truncate">{i.name}</div>
                              <div className="text-xs text-gray-500">{i.sku} · ${i.unit_price.toFixed(2)} ea</div>
                            </div>
                            <div className="flex items-center border border-[var(--border)] rounded"><Button type="button" variant="ghost" size="icon" className="sm:hidden h-8 w-7" onClick={() => setItemQty(i.product_id, i.qty - 1)} data-testid={`cart-qty-down-${i.product_id}`}><ChevronDown size={14} /></Button><Input type="number" min={1} className="w-16 text-center border-0" value={i.qty}
                                   onChange={e => setItemQty(i.product_id, e.target.value)}
                                   data-testid={`cart-qty-${i.product_id}`} /><Button type="button" variant="ghost" size="icon" className="sm:hidden h-8 w-7" onClick={() => setItemQty(i.product_id, i.qty + 1)} data-testid={`cart-qty-up-${i.product_id}`}><ChevronUp size={14} /></Button></div>
                            <div className="w-20 text-right text-sm font-semibold tabular-nums">${(i.qty * i.unit_price).toFixed(2)}</div>
                            <button onClick={() => removeItem(i.product_id)} className="text-red-500 hover:text-red-700"
                                    data-testid={`cart-remove-${i.product_id}`} type="button"><Trash2 size={14} /></button>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-2 border-t border-[var(--border)] font-semibold" data-testid="cart-total">
                          <span>Total ({draft.items.length} item{draft.items.length !== 1 ? "s" : ""})</span>
                          <span className="text-lg text-emerald-600 tabular-nums">${cartTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Step 3 — Notes + submit */}
                  <div className="border border-[var(--border)] rounded-lg p-3 mb-3">
                    <Label className="mb-2 block">3. Notes (optional)</Label>
                    <Textarea rows={2} placeholder="Delivery instructions, urgency, gift-wrap, etc."
                              value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })}
                              data-testid="order-notes" />
                  </div>

                  <Button className="btn-brand w-full" onClick={submitOrder}
                          disabled={submitting || !draft.client_id || draft.items.length === 0}
                          data-testid="new-order-submit">
                    <CartIcon size={14} className="mr-1" />
                    {submitting ? "Creating…" : `Create order · $${createDiscountedTotal.toFixed(2)}`}
                  </Button>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </header>

      <section className="mb-5 border border-amber-200 bg-amber-50/60" data-testid="unpaid-orders-section">
        <button type="button" onClick={() => setUnpaidOpen((open) => !open)} className="flex min-h-14 w-full items-center justify-between gap-4 px-4 text-left" data-testid="unpaid-orders-toggle" aria-expanded={unpaidOpen}>
          <div className="min-w-0"><p className="label-eyebrow text-amber-800">Collections</p><h2 className="text-lg font-bold text-amber-950">Unpaid Orders <span className="ml-1 tabular-nums text-sm font-semibold">({unpaidOrders.length})</span></h2></div>
          <div className="flex items-center gap-3"><div className="text-right"><p className="text-[10px] uppercase tracking-wide text-amber-800">Amount owed</p><p className="font-bold tabular-nums text-amber-950" data-testid="unpaid-orders-total">${unpaidTotal.toFixed(2)}</p></div>{unpaidOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</div>
        </button>
        {unpaidOpen && <div className="border-t border-amber-200 overflow-x-auto"><table className="min-w-[680px] w-full text-sm" data-testid="unpaid-orders-table"><thead className="bg-amber-100/70 text-xs uppercase tracking-wide text-amber-900"><tr><OrderSortHeader {...unpaidSortHeaderProps} column="client" label="Client / DP" testId="unpaid-client" className="sticky left-0 z-20 min-w-[164px] bg-amber-100/70" /><th className="min-w-[84px] bg-amber-100/70 p-3 text-left">Order #</th><OrderSortHeader {...unpaidSortHeaderProps} column="created_at" label="Date" testId="unpaid-date" /><OrderSortHeader {...unpaidSortHeaderProps} column="revenue" label="Amount" testId="unpaid-amount" align="right" /><OrderSortHeader {...unpaidSortHeaderProps} column="status" label="Status" testId="unpaid-status" /></tr></thead><tbody>{unpaidOrders.length === 0 ? <tr><td colSpan="5" className="p-5 text-center text-gray-500">No unpaid orders.</td></tr> : unpaidOrders.map((order) => <tr key={order.id} className="border-t border-amber-100"><td className="sticky left-0 z-10 min-w-[164px] bg-amber-50 p-3"><div className="font-medium">{order.client_name || clientsById.get(order.client_id)?.name || "—"}</div>{order.source === "wholesale-dp-invoice" && <div className="text-xs text-fuchsia-800">Wholesale Club · {order.created_by_name || order.fulfilled_by_name || "DP"}</div>}</td><td className="p-3 font-mono text-xs">{canTakePayment(order) ? <button type="button" onClick={() => takePayment(order)} className="font-mono font-semibold text-[var(--brand)] underline decoration-dotted underline-offset-2 hover:text-red-800" title="Open Take Payment" data-testid={`unpaid-order-payment-${order.id}`}>{order.id?.slice(-8) || "—"}</button> : order.id?.slice(-8) || "—"}</td><td className="p-3 whitespace-nowrap">{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td><td className="p-3 text-right font-semibold tabular-nums">${(Number(order.total) || 0).toFixed(2)}</td><td className="p-3"><StatusPill status={order.status || "Payment Pending"} /></td></tr>)}</tbody></table></div>}
      </section>

      {/* Source filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="source-filter">
        {[
          { k: "all", l: "All orders" },
          { k: "direct_site", l: "Direct Site" },
          { k: "client_portal", l: "Client Portal" },
          { k: "wholesale", l: "Wholesale" },
          { k: "admin", l: "Admin-created" },
        ].map(({ k, l }) => (
          <button
            key={k}
            onClick={() => setSourceFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              sourceFilter === k
                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                : "bg-white border-[var(--border)] text-gray-600 hover:border-gray-400"
            }`}
            data-testid={`source-filter-${k}`}
          >
            {l} <span className="tabular-nums opacity-70 ml-1">({sourceCounts[k] || 0})</span>
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden" id="orders-print">
        <div className="overflow-x-auto">
          <table className="w-full text-sm max-sm:[&_td]:!py-[9px]" data-testid="orders-table">
          <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="p-3 w-10"><SelectAllCheckbox records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /></th>
              <OrderSortHeader {...sortHeaderProps} column="created_at" label="Date" testId="date" className="sm:sticky left-0 bg-[var(--bg-soft)] z-20 min-w-[80px]" />
              <OrderSortHeader {...sortHeaderProps} column="client" label="Client" testId="client" className="sm:sticky left-[80px] bg-[var(--bg-soft)] z-20 min-w-[110px] border-r border-[var(--border)]" />
              <OrderSortHeader {...sortHeaderProps} column="source" label="Source" testId="source" className="min-w-[90px]" />
              <OrderSortHeader {...sortHeaderProps} column="items" label="Items" testId="items" className="hidden md:table-cell" />
              <OrderSortHeader {...sortHeaderProps} column="revenue" label="Revenue" testId="revenue" align="right" />
              <OrderSortHeader {...sortHeaderProps} column="status" label="Status" testId="status" />
              <OrderSortHeader {...sortHeaderProps} column="partner" label="Distributing Partner" testId="partner" className="min-w-[160px]" />
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {paged.map(o => {
              const cli = clientsById.get(o.client_id);
              const src = orderSource(o);
              const srcLabel = o.source === "wholesale-dp-invoice" ? "Wholesale Club" : ORDER_SOURCE_LABELS[src];
              const srcPill = {
                direct_site: "bg-blue-100 text-blue-800 border-blue-200",
                client_portal: "bg-purple-100 text-purple-800 border-purple-200",
                wholesale: "bg-amber-100 text-amber-800 border-amber-200",
                wholesale_club: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
                admin: "bg-gray-100 text-gray-700 border-gray-200",
              }[o.source === "wholesale-dp-invoice" ? "wholesale_club" : src];
              return (
                <tr key={o.id} className="table-row border-t border-[var(--border)] group" data-testid={`order-row-${o.id}`}>
                  <td className="p-3"><SelectionCheckbox checked={selectedIds.includes(o.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, o.id] : selectedIds.filter((id) => id !== o.id))} testId={`select-row-checkbox-${o.id}`} label={`Select order ${o.id}`} /></td>
                  <td className="p-3 sm:sticky left-0 bg-white group-hover:bg-[var(--bg-soft)] z-10 min-w-[80px]" data-testid={`order-date-${o.id}`}>{o.created_at?.slice(0, 10)}</td>
                  <td className="p-3 font-medium capitalize sm:sticky left-[80px] bg-white group-hover:bg-[var(--bg-soft)] z-10 min-w-[110px] border-r border-[var(--border)]" data-testid={`order-client-${o.id}`}>
                    {o.client_name}
                    {cli?.company && <div className="text-xs text-gray-500">{cli.company}</div>}
                  </td>
                  <td className="p-3 min-w-[90px]"><span className={`status-pill ${srcPill}`} data-testid={`order-source-${o.id}`}>{srcLabel}</span></td>
                  <td className="p-3 text-gray-600 max-w-md truncate hidden md:table-cell" data-testid={`order-items-${o.id}`}>{orderItemsText(o)}</td>
                  <td className="p-3 text-right tabular-nums" data-testid={`order-revenue-${o.id}`}><div>${Number(o.total || 0).toFixed(2)}</div>{o.discount?.amount_applied ? <div className="text-[10px] text-emerald-700" data-testid={`order-discount-${o.id}`}>−${Number(o.discount.amount_applied).toFixed(2)} discount</div> : null}{Number(o.tip_amount || o.tip?.amount || 0) > 0 ? <div className="text-[10px] text-amber-700" data-testid={`order-tip-${o.id}`}>+${Number(o.tip_amount || o.tip?.amount || 0).toFixed(2)} tip</div> : null}</td>
                  <td className="p-3">
                    <StatusPill status={o.status} testid={`order-status-${o.id}`} />
                    {o.payment_status === "pending" && (
                      <span
                        className="ml-1 inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-800 align-middle"
                        title={o.payment_link_sent_at ? `Sent ${String(o.payment_link_sent_at).slice(0,10)}` : ""}
                        data-testid={`payment-pending-${o.id}`}
                      >
                        Payment Pending
                      </span>
                    )}
                    {o.payment_status === "paid" && (
                      <span
                        className="ml-1 inline-flex items-center rounded-full bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-emerald-800 align-middle"
                        data-testid={`payment-paid-${o.id}`}
                      >
                        {o.payment_method === "cash" ? "Paid via Cash" : "Paid via Stripe"}
                      </span>
                    )}
                  </td>
                  <td className="p-3 capitalize" data-testid={`order-partner-${o.id}`}>{orderPartnerText(o, cli, partnersById)}</td>
                  <td className="p-3 text-right space-x-2">
                    {canTakePayment(o) && o.payment_status !== "paid" && (
                      <Button
                        size="sm"
                        className="btn-brand"
                        title="Generate a Stripe payment link to take card payment now"
                        onClick={() => takePayment(o)}
                        disabled={payLoadingId === o.id}
                        data-testid={`take-payment-${o.id}`}
                      >
                        <CreditCard size={12} className="mr-1" /> {payLoadingId === o.id ? "…" : "Take payment"}
                      </Button>
                    )}
                    {canManageOrder(o) && o.payment_status !== "paid" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        title="Record a cash payment and calculate change due"
                        onClick={() => openCashPayment(o)}
                        data-testid={`cash-payment-${o.id}`}
                      ><Banknote size={13} className="mr-1" /> Cash</Button>
                    )}
                    {isAdminUser(user) && o.payment_status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        title="Void the previous Stripe link, generate a new one, and email it to the client"
                        onClick={async () => {
                          try {
                            await api.post(`/orders/${o.id}/resend-payment`);
                            toast.success("New payment link emailed to the client");
                            load();
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Resend failed");
                          }
                        }}
                        data-testid={`resend-payment-${o.id}`}
                      >
                        <Send size={12} className="mr-1" /> Resend link
                      </Button>
                    )}
                    {canManageOrder(o) && (
                      <Button size="sm" variant="ghost" onClick={() => openManage(o)} data-testid={`manage-btn-${o.id}`}>
                        <Settings2 size={12} />
                      </Button>
                    )}
                    {canManageOrder(o) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        title={o.payment_status === "paid" ? "Paid orders must be refunded, not deleted" : "Delete order"}
                        onClick={() => openDelete(o)}
                        disabled={o.payment_status === "paid"}
                        data-testid={`delete-order-${o.id}`}
                      ><Trash2 size={14} /></Button>
                    )}
                    {(isAdminUser(user) || (user?.role === "personnel" && o.fulfilled_by === user.id)) && o.status !== "Fulfilled" && o.status?.toLowerCase() !== "fulfilled" && (
                      <>
                        {isAdminUser(user) && !o.fulfilled_by && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => assignNearest(o)}
                            disabled={assigningNearestId === o.id || activeEps.length === 0}
                            title={activeEps.length === 0 ? "No DPs are Go-Active right now" : "Assign nearest active DP"}
                            data-testid={`assign-nearest-${o.id}`}
                            className={activeEps.length > 0 ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : ""}
                          >
                            <MapPin size={12} className="mr-1" />
                            {assigningNearestId === o.id ? "…" : "Nearest DP"}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => { setEtaOrder(o); setEta({ minutes: o.eta_minutes || 30, message: "" }); }} data-testid={`eta-btn-${o.id}`}>ETA</Button>
                        <Button size="sm" className="btn-brand" onClick={() => fulfill(o.id)} data-testid={`fulfill-btn-${o.id}`}>Fulfill</Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-gray-400" data-testid="orders-empty-state">No orders match this filter.</td></tr>}
          </tbody>
        </table>
        </div>
        <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} totalItems={filtered.length} pageSize={PAGE_SIZE} testid="orders-pagination" />
      </Card>
      <BulkActions section="orders" records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />

      <section className="mt-6 border border-slate-200 bg-slate-50" data-testid="archives-section"><button type="button" onClick={() => setArchivesOpen(v => !v)} className="flex min-h-14 w-full items-center justify-between px-4" data-testid="archives-toggle"><span><span className="label-eyebrow">History</span><span className="block text-lg font-bold">Archives ({archiveWeeks.reduce((n, [, rows]) => n + rows.length, 0)})</span></span>{archivesOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</button>{archivesOpen && <div className="border-t border-slate-200">{archiveWeeks.map(([week, rows]) => <div key={week} className="border-b border-slate-200"><button type="button" onClick={() => setOpenArchiveWeeks(v => ({ ...v, [week]: !v[week] }))} className="flex min-h-12 w-full items-center justify-between px-4 text-left text-sm font-semibold" data-testid={`archive-week-${week}`}>Week of {new Date(`${week}T00:00:00`).toLocaleDateString()} <span>{rows.length} {openArchiveWeeks[week] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span></button>{openArchiveWeeks[week] && <div className="overflow-x-auto"><table className="min-w-[620px] w-full text-sm"><tbody>{rows.map(order => <tr key={order.id} className="border-t border-slate-100"><td className="p-3 font-mono text-xs"><button type="button" onClick={() => setManageOrder({ ...order, _readOnly: true })} className="text-[var(--brand)] underline" data-testid={`archive-view-${order.id}`}>{order.id?.slice(-8)}</button></td><td className="p-3">{order.client_name || clientsById.get(order.client_id)?.name || "—"}</td><td className="p-3">{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td><td className="p-3 text-right">${(Number(order.total) || 0).toFixed(2)}</td><td className="p-3"><button type="button" onClick={() => restoreArchivedOrder(order)} className="text-left" title="Change to Ordered" data-testid={`archive-restore-${order.id}`}><StatusPill status={order.status} /></button></td></tr>)}</tbody></table></div>}</div>)}</div>}</section>

      <Dialog open={!!archiveViewOrder} onOpenChange={(open) => !open && setArchiveViewOrder(null)}><DialogContent className="max-w-lg" data-testid="archive-read-only-dialog"><DialogHeader><DialogTitle>Archived Order #{archiveViewOrder?.id?.slice(-8)}</DialogTitle></DialogHeader>{archiveViewOrder && <div className="space-y-3 text-sm"><div className="grid grid-cols-2 gap-3"><div><p className="text-xs text-gray-500">Client</p><p>{archiveViewOrder.client_name || clientsById.get(archiveViewOrder.client_id)?.name || "—"}</p></div><div><p className="text-xs text-gray-500">Total</p><p className="font-semibold">${(Number(archiveViewOrder.total) || 0).toFixed(2)}</p></div></div><div><p className="text-xs text-gray-500">Items</p>{(archiveViewOrder.items || []).map((item, index) => <p key={index}>{item.qty} × {item.name || item.product_name || "Item"}</p>)}</div><StatusPill status={archiveViewOrder.status} /></div>}</DialogContent></Dialog>

      <Dialog open={!!etaOrder} onOpenChange={(o) => !o && setEtaOrder(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send delivery ETA</DialogTitle><DialogDescription>Share an estimated delivery window with the selected client.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="label-eyebrow">Minutes</label><Input type="number" value={eta.minutes} onChange={e => setEta({ ...eta, minutes: e.target.value })} data-testid="eta-minutes-input" /></div>
            <div><label className="label-eyebrow">Message</label><Input value={eta.message} onChange={e => setEta({ ...eta, message: e.target.value })} placeholder="On my way!" data-testid="eta-message-input" /></div>
            <Button className="btn-brand w-full" onClick={sendEta} data-testid="eta-send">Send ETA</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* GPS Dispatch Map — live view of Go-Active DPs */}
      <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio size={16} className={activeEps.length > 0 ? "text-emerald-600" : "text-gray-400"} />
              GPS Dispatch · {activeEps.length} DP{activeEps.length === 1 ? "" : "s"} live
            </DialogTitle>
            <DialogDescription>View active distributing partners and assign the order for delivery.</DialogDescription>
          </DialogHeader>
          {activeEps.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              <MapPin size={20} className="inline-block mb-2 text-gray-400" />
              <p>No Distributing Partners are Go-Active right now.</p>
              <p className="text-xs text-gray-400 mt-1">DPs flip on their <b>Live Dispatch Status</b> toggle on the dashboard to appear here.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md overflow-hidden border border-[var(--border)]" style={{ height: 380 }}>
                <MapContainer
                  center={[activeEps[0].lat, activeEps[0].lng]}
                  zoom={10}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {activeEps.map((ep) => (
                    <Marker
                      key={ep.id}
                      position={[ep.lat, ep.lng]}
                      icon={L.divIcon({
                        className: "",
                        html: `<div style="background:${ep.color};width:32px;height:32px;border:3px solid #fff;border-radius:999px;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:14px">${ep.icon_glyph || "🚚"}</div>`,
                        iconSize: [32, 32],
                        iconAnchor: [16, 16],
                      })}
                    >
                      <Popup>
                        <div className="text-xs">
                          <div className="font-bold capitalize">{ep.name}</div>
                          {ep.role && <div className="text-[10px] uppercase tracking-widest text-red-700">{ep.role}</div>}
                          <div className="text-gray-500 tabular-nums">
                            {Number(ep.lat).toFixed(4)}, {Number(ep.lng).toFixed(4)}
                          </div>
                          <div className="text-[10px] text-gray-400 mt-1">
                            Last ping · {ep.last_ping_at ? new Date(ep.last_ping_at).toLocaleTimeString() : "—"}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  {activeEps.map((ep) => (
                    <CircleMarker
                      key={`ping-${ep.id}`}
                      center={[ep.lat, ep.lng]}
                      radius={14}
                      pathOptions={{ color: "#10b981", weight: 2, opacity: 0.7, fillOpacity: 0 }}
                    />
                  ))}
                </MapContainer>
              </div>

              <div className="mt-4">
                <p className="label-eyebrow mb-2">Pending orders — click Nearest to auto-assign</p>
                <div className="max-h-[220px] overflow-y-auto divide-y divide-[var(--border)] border border-[var(--border)] rounded-md">
                  {orders
                    .filter((o) => !o.fulfilled_by && o.status !== "Fulfilled" && o.status?.toLowerCase() !== "fulfilled")
                    .slice(0, 25)
                    .map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`dispatch-row-${o.id}`}>
                        <div className="min-w-0">
                          <div className="font-mono text-[10px] text-gray-400">{o.id.slice(0, 8)}</div>
                          <div className="capitalize font-medium truncate">{o.client_name || "—"}</div>
                          <div className="text-[10px] text-gray-500 tabular-nums">${Number(o.total || 0).toFixed(2)} · {o.status}</div>
                        </div>
                        <Button
                          size="sm"
                          className="btn-brand shrink-0"
                          onClick={() => assignNearest(o)}
                          disabled={assigningNearestId === o.id}
                          data-testid={`dispatch-assign-${o.id}`}
                        >
                          <MapPin size={12} className="mr-1" />
                          {assigningNearestId === o.id ? "Assigning…" : "Assign Nearest DP"}
                        </Button>
                      </div>
                    ))}
                  {orders.filter((o) => !o.fulfilled_by && o.status !== "Fulfilled" && o.status?.toLowerCase() !== "fulfilled").length === 0 && (
                    <div className="p-4 text-center text-gray-400 text-xs">Every open order already has a DP assigned.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage order — status change, reassign warehouse ↔ DP, notes */}
      <Dialog open={!!manageOrder} onOpenChange={(o) => !o && setManageOrder(null)}>
        <DialogContent className={`max-w-xl max-h-[92vh] overflow-y-auto ${manageOrder?._readOnly ? "[&_input]:pointer-events-none [&_textarea]:pointer-events-none [&_select]:pointer-events-none [&_button]:pointer-events-none [&_button.absolute]:!pointer-events-auto" : ""}`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 size={16} /> Manage order</DialogTitle><DialogDescription>Update order details, record a required change reason, or manage payment actions.</DialogDescription></DialogHeader>
          {manageOrder && (
            <div className="space-y-4 text-sm">
              <div className="text-xs text-gray-500">
                <span className="font-mono">{manageOrder.id?.slice(0, 8)}</span> · <span className="capitalize font-medium text-gray-700">{manageOrder.client_name}</span> · <span className="font-semibold">${manageTotal.toFixed(2)}</span>
              </div>
              <div className="border-y border-[var(--border)] py-3 space-y-1 text-xs" data-testid="manage-order-summary">
                <div className="flex justify-between text-gray-500"><span>Subtotal</span><span className="tabular-nums">${manageSubtotal.toFixed(2)}</span></div>
                {manageOrder.discount?.amount_applied ? <div className="flex justify-between text-emerald-700 font-medium" data-testid="manage-order-discount-line"><span>Discount · {manageOrder.discount.code || manageOrder.discount.reason}</span><span className="tabular-nums">−${managedDiscountAmount.toFixed(2)}</span></div> : null}
                {manageTipAmount > 0 ? <div className="flex justify-between text-amber-800 font-medium" data-testid="manage-order-tip-line"><span>{manageOrder.tip?.label || "Tip"}{manageOrder.tip_attributed_to_name ? ` · ${manageOrder.tip_attributed_to_name}` : " · awaiting assignment"}</span><span className="tabular-nums">${manageTipAmount.toFixed(2)}</span></div> : null}
                <OrderStripeEvents orderId={manageOrder.id} />
                <div className="flex justify-between text-sm font-bold"><span>Order total</span><span className="tabular-nums">${manageTotal.toFixed(2)}</span></div>
              </div>
              <OrderPaymentReferences order={manageOrder} />
              {manageOrder.payment_method === "cash" && <div className="text-xs border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-3 space-y-1" data-testid="cash-payment-receipt"><div className="font-semibold flex items-center gap-1"><Banknote size={13} /> Cash payment received</div><div className="flex justify-between"><span>Amount received</span><span className="tabular-nums">${Number(manageOrder.cash_received || 0).toFixed(2)}</span></div><div className="flex justify-between"><span>Change due</span><span className="tabular-nums">${Number(manageOrder.change_due || 0).toFixed(2)}</span></div><div className="text-[10px] text-emerald-800">Cash / Out of Band · {manageOrder.cash_received_at ? new Date(manageOrder.cash_received_at).toLocaleString() : "Recorded"}</div></div>}
              {isImmutable && (
                <div className="text-[11px] p-2 bg-blue-50 border border-blue-200 rounded text-blue-800">
                  Paid order — line items are locked, but status, notes, and fulfiller can still change.
                </div>
              )}

              <section className="border border-[var(--border)] rounded-md p-3 space-y-3" data-testid="manage-line-items-section">
                <div className="flex items-center justify-between"><Label className="font-semibold">Line items</Label><span className="text-[11px] text-gray-500">Prices update from current inventory on save</span></div>
                <div className="space-y-2">
                  {manageItems.map((item) => <div key={item.product_id} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2 items-center border-b border-[var(--border)] pb-2 last:border-0 last:pb-0" data-testid={`manage-line-item-${item.product_id}`}><div className="min-w-0"><p className="font-medium truncate">{item.name}</p><p className="text-[11px] text-gray-500">${Number(item.unit_price || 0).toFixed(2)} each</p></div><div className="flex items-center border border-[var(--border)] rounded"><Button type="button" variant="ghost" size="icon" disabled={isImmutable} onClick={() => updateManageQty(item.product_id, Number(item.qty) - 1)} data-testid={`manage-item-minus-${item.product_id}`}><Minus size={13} className="hidden sm:block" /><ChevronDown size={14} className="sm:hidden" /></Button><Input type="number" min="1" disabled={isImmutable} value={item.qty} onChange={(e) => updateManageQty(item.product_id, Number(e.target.value) || 0)} className="w-12 h-8 border-0 text-center px-1" data-testid={`manage-item-qty-${item.product_id}`} /><Button type="button" variant="ghost" size="icon" disabled={isImmutable} onClick={() => updateManageQty(item.product_id, Number(item.qty) + 1)} data-testid={`manage-item-plus-${item.product_id}`}><Plus size={13} className="hidden sm:block" /><ChevronUp size={14} className="sm:hidden" /></Button></div><Button type="button" size="icon" variant="ghost" disabled={isImmutable} className="text-red-600 hover:text-red-700" title="Remove line item" onClick={() => updateManageQty(item.product_id, 0)} data-testid={`manage-item-remove-${item.product_id}`}><Trash2 size={14} /></Button></div>)}
                  {!manageItems.length && <p className="text-xs text-red-700" data-testid="manage-empty-line-items">Add a product before saving this order.</p>}
                </div>
                {!isImmutable && <div className="relative border-t border-[var(--border)] pt-3"><Label htmlFor="manage-product-search">Add product</Label><div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 mt-1"><Input id="manage-product-search" value={manageProductQuery} onChange={(e) => setManageProductQuery(e.target.value)} placeholder="Search products or SKU" data-testid="manage-product-search" /><div className="flex items-center border border-[var(--border)] rounded"><Button type="button" variant="ghost" size="icon" className="sm:hidden h-9 w-7" onClick={() => setManageAddQty(Math.max(1, Number(manageAddQty) - 1))} data-testid="manage-add-qty-down"><ChevronDown size={14} /></Button><Input type="number" min="1" value={manageAddQty} onChange={(e) => setManageAddQty(e.target.value)} className="w-14 border-0 text-center" data-testid="manage-add-qty" /><Button type="button" variant="ghost" size="icon" className="sm:hidden h-9 w-7" onClick={() => setManageAddQty(Number(manageAddQty) + 1)} data-testid="manage-add-qty-up"><ChevronUp size={14} /></Button></div></div>{manageProductQuery && <div className="mt-1 max-h-40 overflow-y-auto border border-[var(--border)] rounded bg-white shadow-sm" data-testid="manage-product-suggestions">{matchedManageProducts.map((product) => <button key={product.id} type="button" onClick={() => addManageProduct(product)} className="w-full flex justify-between gap-3 p-2 text-left hover:bg-gray-50 border-b border-[var(--border)] last:border-0" data-testid={`manage-add-product-${product.id}`}><span className="truncate">{product.name}</span><span className="text-xs text-gray-500 shrink-0">${Number(product.wholesale_price || 0).toFixed(2)}</span></button>)}{!matchedManageProducts.length && <p className="p-2 text-xs text-gray-500">No products found.</p>}</div>}</div>}
              </section>

              <div>
                <Label>Status</Label>
                <select
                  className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white capitalize"
                  value={manageStatus} onChange={(e) => setManageStatus(e.target.value)}
                  data-testid="manage-status"
                >
                  {ORDER_STATUS_OPTIONS.map((s) => <option key={s} value={s} data-testid={`manage-status-option-${s.replace(/\s/g, "-")}`}>{s === "dropped off" ? "Dropped Off" : s}</option>)}
                </select>
              </div>

              <div>
                <Label className="flex items-center gap-1"><ArrowRightLeft size={12} /> Fulfilled by</Label>
                <select
                  className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white capitalize"
                  value={manageFulfiller} onChange={(e) => setManageFulfiller(e.target.value)}
                  disabled={!isAdminUser(user)}
                  data-testid="manage-fulfiller"
                >
                  <option value="warehouse">Warehouse</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.company ? ` — ${p.company}` : ""}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  Reassignment is operational — inventory was decremented at sale time and is not re-adjusted here.
                </p>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea value={manageNotes} onChange={(e) => setManageNotes(e.target.value)} rows={3} data-testid="manage-notes" />
              </div>

              <div>
                <Label htmlFor="modification-justification">Modification justification *</Label>
                <select id="modification-justification" className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white text-sm mt-1" value={modificationJustification} onChange={(e) => setModificationJustification(e.target.value)} data-testid="modification-justification-select">
                  <option value="">Select a reason</option>
                  {MODIFICATION_JUSTIFICATIONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                </select>
              </div>

              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2" data-testid="manual-discount-panel">
                <Label className="flex items-center gap-1 text-amber-900"><BadgePercent size={13} /> Manual order discount</Label>
                <div className="grid grid-cols-[1fr_100px] gap-2"><select className="h-9 border border-amber-200 rounded px-2 bg-white text-sm" value={manualDiscount.discount_type} onChange={(e) => setManualDiscount({ ...manualDiscount, discount_type: e.target.value })} data-testid="manual-discount-type"><option value="percentage">Percent off</option><option value="fixed">Dollar amount off</option></select><Input type="number" min="0.01" max={manualDiscount.discount_type === "percentage" ? "100" : undefined} step="0.01" value={manualDiscount.amount} onChange={(e) => setManualDiscount({ ...manualDiscount, amount: e.target.value })} placeholder="Amount" data-testid="manual-discount-amount" /></div>
                <Input value={manualDiscount.reason} onChange={(e) => setManualDiscount({ ...manualDiscount, reason: e.target.value })} placeholder="Reason for this adjustment" data-testid="manual-discount-reason" />
                <Button variant="outline" className="w-full border-amber-300 text-amber-900 hover:bg-amber-100" onClick={applyManualDiscount} disabled={discountSaving || manageOrder.payment_status === "paid"} data-testid="apply-manual-discount-button">{discountSaving ? "Applying…" : "Apply manual discount"}</Button>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="shrink-0 text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50" title="Delete order" disabled={manageOrder.payment_status === "paid"} onClick={() => openDelete(manageOrder)} data-testid="manage-delete-order-button"><Trash2 size={15} /></Button>
                {!manageOrder._readOnly && <Button className="btn-brand flex-1" onClick={saveManage} data-testid="manage-save">Save changes</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cashOrder} onOpenChange={(open) => { if (!open) setCashOrder(null); }}>
        <DialogContent data-testid="cash-payment-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-800"><Banknote size={18} /> Record cash payment</DialogTitle><DialogDescription>Confirm the cash collected before marking this order paid. A Stripe invoice will be recorded as Cash / Out of Band.</DialogDescription></DialogHeader>
          {cashOrder && <div className="space-y-4">
            <div className="border-y border-[var(--border)] py-3 text-sm space-y-1" data-testid="cash-payment-summary"><div className="flex justify-between"><span>Order total</span><span className="font-bold tabular-nums">${cashDue.toFixed(2)}</span></div><div className="text-xs text-gray-500 capitalize">{cashOrder.client_name} · Order #{String(cashOrder.id).slice(0, 8).toUpperCase()}</div></div>
            <div><Label htmlFor="cash-received">Cash amount received *</Label><Input id="cash-received" type="number" min={cashDue.toFixed(2)} step="0.01" value={cashReceived} onChange={(e) => setCashReceived(e.target.value)} data-testid="cash-received-input" /></div>
            <div className={`flex justify-between p-3 rounded-md text-sm ${cashAmount >= cashDue ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-800"}`} data-testid="cash-change-due"><span>Change due</span><span className="font-bold tabular-nums">${changeDue.toFixed(2)}</span></div>
            <div><Label htmlFor="cash-payment-note">Cash note (optional)</Label><Input id="cash-payment-note" value={cashNote} onChange={(e) => setCashNote(e.target.value)} placeholder="Receipt or drawer reference" data-testid="cash-payment-note-input" /></div>
            <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" disabled={cashSaving || cashAmount < cashDue} onClick={confirmCashPayment} data-testid="confirm-cash-payment-button">{cashSaving ? "Recording…" : `Confirm cash · $${cashAmount.toFixed(2)}`}</Button>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-order-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 size={17} /> Delete client order</DialogTitle><DialogDescription>This removes the order from active Client Orders and records the selected justification.</DialogDescription></DialogHeader>
          {deleteTarget && <div className="space-y-4">
            <div className="text-sm text-gray-600" data-testid="delete-order-summary"><span className="font-semibold capitalize text-gray-900">{deleteTarget.client_name}</span> · Order #{String(deleteTarget.id).slice(0, 8).toUpperCase()} · <span className="tabular-nums">${Number(deleteTarget.total || 0).toFixed(2)}</span></div>
            <div><Label htmlFor="delete-justification">Deletion justification *</Label><select id="delete-justification" className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white text-sm mt-1" value={deleteJustification} onChange={(e) => setDeleteJustification(e.target.value)} data-testid="delete-justification-select"><option value="">Select a reason</option>{DELETION_JUSTIFICATIONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}</select></div>
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={deleteSaving || !deleteJustification} onClick={deleteOrder} data-testid="confirm-delete-order-button">{deleteSaving ? "Deleting…" : "Delete order"}</Button>
          </div>}
        </DialogContent>
      </Dialog>

      {/* Real-time payment link — copy or open Stripe checkout on the spot */}
      <Dialog open={!!payOrder} onOpenChange={(o) => { if (!o) { setPayOrder(null); setPayUrl(""); } }}>
        <DialogContent data-testid="take-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard size={18} className="text-[var(--brand)]" /> Take payment</DialogTitle>
            <DialogDescription>Share or open a secure Stripe checkout link to collect payment for this order.</DialogDescription>
          </DialogHeader>
          {payOrder && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <div><span className="font-semibold text-gray-900 capitalize">{payOrder.client_name}</span> · Order #{String(payOrder.id).slice(0, 8).toUpperCase()}</div>
                {payOrder.discount?.amount_applied ? <div className="mt-2 text-xs space-y-0.5" data-testid="payment-order-discount-summary"><div className="flex justify-between"><span>Subtotal</span><span>${Number(payOrder.subtotal || 0).toFixed(2)}</span></div><div className="flex justify-between text-emerald-700"><span>Discount · {payOrder.discount.code || payOrder.discount.reason}</span><span>−${Number(payOrder.discount.amount_applied).toFixed(2)}</span></div></div> : null}
                <div className="text-lg font-bold tabular-nums text-gray-900 mt-1">${Number(payOrder.total || 0).toFixed(2)} due</div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Secure Stripe checkout link</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={payUrl} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="payment-link-input" />
                  <Button variant="outline" size="icon" onClick={copyPayUrl} title="Copy link" data-testid="copy-payment-link">
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Copy and text/email it to the client, or open it here to enter their card and charge on the spot.</p>
              </div>
              <div className="flex gap-2">
                <Button className="btn-brand flex-1" onClick={() => window.open(payUrl, "_blank", "noopener,noreferrer")} data-testid="open-checkout-btn">
                  <ExternalLink size={14} className="mr-1.5" /> Open checkout
                </Button>
                <Button variant="outline" onClick={copyPayUrl} data-testid="copy-payment-link-2">
                  <Copy size={14} className="mr-1.5" /> Copy link
                </Button>
              </div>
              <p className="text-[11px] text-gray-400 text-center">Once paid, this order flips to <strong className="text-emerald-600">Paid via Stripe</strong> automatically.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
